import { useMemo, useState } from 'react';
import { QUESTION_PERMISSIONS } from '@/constants/permissions';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useQuery } from '@tanstack/react-query';
import { FIRST_PAGE } from '@/api/pagination';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { LabeledPillList } from '@/components/common/LabeledPill';
import { TableTabs, type TableTab } from '@/components/common/TableTabs';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { Button } from '@/components/ui/button';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import { parseNumberFilter } from '@/utils/filters';
import {
  assignmentSubjectsQueryOptions,
  myAssignmentsQueryOptions,
  myQuestionProjectsInfiniteQueryOptions,
} from '../api/question.api';
import { PaperManagerDialog } from '../components/PaperManagerDialog';
import { questionProjectsQueryOptions } from '../api/question.api';
import { CollectionProjectsTable } from '../components/CollectionProjectsTable';
import { DetailCard } from '../components/DetailCard';
import { getMyAssignmentColumns, getUploadSubjectColumns } from '../components/QuestionColumns';
import { PROJECT_LOOKUP_PAGE_SIZE } from '../constants/question.constants';
import { useAssignedInstituteFields } from '../hooks/useAssignedInstituteFields';
import { useProjectFilterField } from '../hooks/useProjectFilterField';
import { useUploadFilters } from '../hooks/useUploadFilters';
import { filterAssignments } from '../utils/filterAssignments';
import { getProjectPillFields } from '../utils/projectPills';
import type { Assignment, UploadSubject } from '../types/question.types';

type UploadTabId = 'institutes' | 'subjects';

interface PickedInstitute {
  projectId: number | undefined;
  institute: Assignment;
}

export function QuestionUploadPage() {
  const { can } = usePermissions();
  const controller = useUploadFilters();
  const { search } = controller;
  const projectField = useProjectFilterField();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeTabId, setActiveTabId] = useState<UploadTabId>('institutes');
  const [picked, setPicked] = useState<PickedInstitute | null>(null);
  const [subject, setSubject] = useState<UploadSubject | null>(null);

  const hasProject = search.projectId !== undefined;

  const declaredProjects = useInfiniteTableQuery(myQuestionProjectsInfiniteQueryOptions());
  const assignments = useQuery(myAssignmentsQueryOptions(search.projectId));
  const projects = useQuery(
    questionProjectsQueryOptions({ page: FIRST_PAGE, pageSize: PROJECT_LOOKUP_PAGE_SIZE }),
  );
  const selectedProject = projects.data?.data.find(
    (project) => project.projectId === search.projectId,
  );

  const shown = useMemo(
    () => filterAssignments(assignments.data ?? [], search),
    [assignments.data, search],
  );
  const assignmentsInFilters = useQuery(
    myAssignmentsQueryOptions(parseNumberFilter(controller.filterValues.projectId)),
  );
  const instituteFields = useAssignedInstituteFields(assignmentsInFilters.data ?? []);
  const pickedInFilter = shown.find((row) => row.instituteId === search.instituteId);

  const institute =
    picked !== null && picked.projectId === search.projectId
      ? picked.institute
      : (pickedInFilter ?? null);

  const subjects = useQuery(assignmentSubjectsQueryOptions(institute?.projectInstituteId));

  function handleInstituteOpen(nextInstitute: Assignment) {
    setPicked({ projectId: search.projectId, institute: nextInstitute });
    setSubject(null);
    setActiveTabId('subjects');
  }

  const tabs: TableTab<UploadTabId>[] = [
    { id: 'institutes', label: 'Institutes', count: shown.length },
    {
      id: 'subjects',
      label: 'Subjects',
      count: institute === null ? 0 : subjects.data?.length,
      isCountLoading: institute !== null && subjects.isLoading,
    },
  ];

  function renderInstitutesTable() {
    if (assignments.error) {
      return (
        <ErrorState
          error={assignments.error}
          fallbackTitle="Could not load your institutes"
          onRetry={() => void assignments.refetch()}
          fill
        />
      );
    }
    return (
      <DataTable
        data={shown}
        columns={getMyAssignmentColumns(handleInstituteOpen, selectedProject?.deadlineDate ?? null)}
        isLoading={assignments.isLoading}
        className="lg:flex-1"
        stickyColumnId="instituteName"
        emptyMessage={
          (assignments.data?.length ?? 0) === 0
            ? 'No institute on this project is assigned to you'
            : 'No assigned institute matches these filters'
        }
        emptyHint={
          (assignments.data?.length ?? 0) === 0
            ? 'Institutes appear here once they are assigned to you on this project.'
            : 'Clear or widen the institute filters to see more.'
        }
      />
    );
  }

  function renderSubjectsTable() {
    if (institute === null) {
      return (
        <EmptyState
          kind="pickFirst"
          title="Pick an institute"
          description="Open an institute from the Institutes tab to see its subjects."
          actions={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setActiveTabId('institutes')}
            >
              Go to Institutes
            </Button>
          }
          fill
        />
      );
    }
    if (subjects.error) {
      return (
        <ErrorState
          error={subjects.error}
          fallbackTitle="Could not load the subjects"
          onRetry={() => void subjects.refetch()}
          fill
        />
      );
    }
    return (
      <>
        <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          {institute.instituteName ?? 'Institute'}
        </h2>
        <DataTable
          data={subjects.data ?? []}
          columns={getUploadSubjectColumns(setSubject, can(QUESTION_PERMISSIONS.UPLOAD_CREATE))}
          isLoading={subjects.isLoading}
          className="lg:flex-1"
          stickyColumnId="subjectName"
          emptyMessage="No subject is set for this institute"
          emptyHint="Subjects appear here once they are set on this assignment."
        />
      </>
    );
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Question Upload"
        actions={
          hasProject ? (
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
          ) : null
        }
      />

      {hasProject ? (
        <FilterPanelCollapse isOpen={isFilterOpen}>
          <AdvancedFilterPanel
            fields={[projectField, ...instituteFields]}
            {...getFilterPanelProps(controller)}
          />
        </FilterPanelCollapse>
      ) : null}

      {selectedProject === undefined ? null : (
        <LabeledPillList fields={getProjectPillFields(selectedProject)} />
      )}

      {pickedInFilter === undefined ? null : (
        <div className="flex flex-wrap gap-3">
          <DetailCard
            title="Institution"
            rows={[
              ['Institute', pickedInFilter.instituteName],
              [
                'Code',
                pickedInFilter.instituteCode === null ? null : String(pickedInFilter.instituteCode),
              ],
              ['EIIN', pickedInFilter.instituteEiin],
              ['Division', pickedInFilter.divisionName],
              ['District', pickedInFilter.districtName],
              ['Thana', pickedInFilter.thanaName],
            ]}
          />
        </div>
      )}

      {hasProject ? (
        <>
          <TableTabs
            tabs={tabs}
            activeTabId={activeTabId}
            onTabChange={setActiveTabId}
            ariaLabel="Upload tables"
          />
          {activeTabId === 'institutes' ? renderInstitutesTable() : renderSubjectsTable()}
        </>
      ) : declaredProjects.loadError ? (
        <ErrorState
          error={declaredProjects.loadError}
          fallbackTitle="Could not load the declared projects"
          onRetry={() => void declaredProjects.refetch()}
          fill
        />
      ) : (
        <>
          <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
            Declared Projects
          </h2>
          <CollectionProjectsTable
            projects={declaredProjects.rows}
            isLoading={declaredProjects.isLoading}
            onSelect={(projectId) => {
              void controller.navigateToProject(projectId);
            }}
            openOnly
            emptyMessage="No project is declared for you to upload against"
            emptyHint="Declared projects appear here once one is open and assigned to you."
            loadMoreProps={declaredProjects.loadMoreProps}
          />
        </>
      )}

      <PaperManagerDialog
        subject={subject}
        instituteName={institute?.instituteName ?? null}
        canWrite
        onOpenChange={(open) => {
          if (!open) setSubject(null);
        }}
      />
    </div>
  );
}
